import * as React from 'react';
import type {Except} from 'type-fest';
import {Composite} from '@floating-ui/react';
import {noop} from 'lodash';

import type {SelectionModeProps} from '../../hooks';
import {HStack, VStack} from '../Stack';
import {useSelectionMode} from '../../hooks';

import type {StackProps} from '../Stack';
import type {RowItemBaseProps} from '../RowItem';

type CompositeProps = React.ComponentProps<typeof Composite>;

export type ListBoxProps = {
  /**
   * The index of the active item in the ListBox.
   * Used to control the active item in the ListBox.
   *
   * Please be aware that this does _not_ prevent keyboard navigation or the ListBox from updating the active item.
   * To keep an external activeIndex updated with keyboard navigation, you must provide an `onNavigation` callback to
   * update your own state.
   */
  activeIndex?: CompositeProps['activeIndex'];
  /**
   * The visual style for the checkmark. This value can be overriden by individual `ListBoxItem` components.
   * Defaults to `default`.
   */
  checkmarkStyle?: RowItemBaseProps['checkmarkStyle'];
  /**
   * The index of the first item to be focused when the ListBox received focus.
   *
   * Defaults to `0`.
   */
  initialIndex?: number;
  /**
   * An accessible label for the ListBox.
   */
  label?: string;
  /**
   * Callback for when the active item changes. This is useful for updating the active index in an external state.
   */
  onNavigate?: CompositeProps['onNavigate'];
  /**
   * Set the orientation of the `ListBox`.
   * If set to `horizontal`, the `ListBox` will be a horizontal list.
   *
   * Defaults to `vertical`.
   */
  orientation?: CompositeProps['orientation'];
  /**
   * Whether the ListBox should loop when reaching the end of the list.
   */
  shouldLoop?: CompositeProps['loop'];
  /**
   * Whether the ListBox should set the active item when the mouse enters an item.
   */
  shouldSetActiveOnMouseEnter?: boolean;
} & SelectionModeProps &
  Except<StackProps, 'onChange' | 'direction'>;

type ListBoxContextValue = {
  checkmarkStyle: RowItemBaseProps['checkmarkStyle'];
  isInListBox: boolean;
  listChildRole?: React.AriaRole;
  selectionMode: SelectionModeProps['selectionMode'];
  activeIndex: number;
  onNavigate: (index: number) => void;
  getIsSelected: (value: string) => boolean;
  shouldSetActiveOnMouseEnter?: boolean;
  toggleSelectedValue: (value: string) => void;
};

const ListBoxContext = React.createContext<ListBoxContextValue | null>({
  checkmarkStyle: 'default',
  isInListBox: false,
  listChildRole: 'option',
  selectionMode: 'single',
  activeIndex: 0,
  onNavigate: noop,
  getIsSelected: () => false,
  shouldSetActiveOnMouseEnter: false,
  toggleSelectedValue: noop,
});

export const useListBoxContext = () => {
  const context = React.useContext(ListBoxContext);
  if (!context) {
    throw new Error('You must use ListBoxItem within ListBox');
  }
  return context;
};

type UseListBoxProps = {
  initialIndex: ListBoxProps['initialIndex'];
  onNavigate: ListBoxProps['onNavigate'];
} & SelectionModeProps;

const useListBox = ({
  initialIndex,
  onNavigate: onNavigateProp,
  onChange,
  selectionMode,
  value,
}: UseListBoxProps) => {
  // We are syncing our navigation so that we can expose our useListBoxItem hook to consumers.
  const [activeIndex, setActiveIndex] = React.useState(initialIndex ?? 0);
  const onNavigate = React.useCallback(
    (index: number) => {
      setActiveIndex(index);
      onNavigateProp?.(index);
    },
    [onNavigateProp]
  );

  const {toggleSelectedValue, getIsSelected} =
    // @ts-expect-error - For some reason, typescript can't pass discriminated union types through to discriminated union types. ¯\_(ツ)_/¯
    useSelectionMode({onChange, selectionMode, value});

  return React.useMemo(
    () => ({
      activeIndex,
      onNavigate,
      toggleSelectedValue,
      getIsSelected,
    }),
    [activeIndex, onNavigate, toggleSelectedValue, getIsSelected]
  );
};

/**
 * # ListBox
 *
 * `ListBox` is used to add keyboard navigation and behavior to a list of items.
 * Child items **must** be wrapped in a `ListBoxItem` component to be navigable.
 *
 * ## Usage
 *
 * ```jsx
 * import {ListBox, ListBox} from '@packages/systems/spring';
 *
 * const MyComponent = () => (
 *  <ListBox>
 *    <ListBoxItem asChild>
 *      <Button>Button 1</Button>
 *    </ListBoxItem>
 *    <ListBoxItem>
 *        <HStack>
 *        <Button>Button 2</Button>
 *        <Button>Button 3</Button>
 *      </HStack>
 *    </ListBoxItem>
 *  </ListBox>
 * );
 * ```
 * @component
 */
export const ListBox = ({
  checkmarkStyle,
  children,
  initialIndex,
  label: labelProp,
  shouldLoop = true,
  onChange,
  onNavigate: onNavigateProp,
  selectionMode = 'single',
  spacing: spacingProp = 0,
  shouldSetActiveOnMouseEnter,
  value,
  'aria-label': ariaLabelProp,
  ...props
}: ListBoxProps) => {
  const {activeIndex, onNavigate, toggleSelectedValue, getIsSelected} =
    // @ts-expect-error - For some reason, typescript can't pass discriminated union types through to discriminated union types. ¯\_(ツ)_/¯
    useListBox({
      initialIndex,
      onNavigate: onNavigateProp,
      onChange,
      selectionMode,
      value,
    });

  const contextValue = React.useMemo(
    () => ({
      checkmarkStyle,
      isInListBox: true,
      listChildRole: selectionMode === 'none' ? undefined : 'option',
      activeIndex,
      onNavigate,
      selectionMode,
      shouldSetActiveOnMouseEnter,
      toggleSelectedValue,
      getIsSelected,
    }),
    [
      checkmarkStyle,
      selectionMode,
      activeIndex,
      onNavigate,
      shouldSetActiveOnMouseEnter,
      toggleSelectedValue,
      getIsSelected,
    ]
  );

  const Wrapper = props.orientation === 'horizontal' ? HStack : VStack;

  return (
    <ListBoxContext.Provider value={contextValue}>
      <Wrapper {...props} spacing={spacingProp}>
        <Composite
          activeIndex={activeIndex}
          aria-label={ariaLabelProp ?? labelProp}
          aria-multiselectable={
            selectionMode === 'multiple' ? 'true' : undefined
          }
          loop={shouldLoop}
          onNavigate={onNavigate}
          role={selectionMode === 'none' ? undefined : 'listbox'}
          style={{display: 'contents'}}
        >
          {children}
        </Composite>
      </Wrapper>
    </ListBoxContext.Provider>
  );
};

ListBox.displayName = 'ListBox';

export default ListBox;
